import React, { useState, useEffect } from 'react';
import { creditPurchasesStorage } from '../../services/localStorage.service';
import './CreditPurchases.css';

// Icon Component
const DeleteIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.75 4.5H14.25M7.5 7.5V12.75M10.5 7.5V12.75M5.25 4.5L5.625 13.5C5.625 14.1213 6.12868 14.625 6.75 14.625H11.25C11.8713 14.625 12.375 14.1213 12.375 13.5L12.75 4.5M6.75 4.5V3C6.75 2.58579 7.08579 2.25 7.5 2.25H10.5C10.9142 2.25 11.25 2.58579 11.25 3V4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CreditPurchases = () => {
  const [formData, setFormData] = useState({
    distributorName: 'Meher Distributors Pvt. Ltd',
    contactNumber: '022-61310000',
    paymentDate: '2025-11-07',
    amount: '15000.00',
    paymentType: 'Cash',
    notes: '',
  });

  const [remainingPayments, setRemainingPayments] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadRemainingPayments();
  }, []);

  const loadRemainingPayments = () => {
    // Load all credit purchases
    const payments = creditPurchasesStorage.getAllCreditPurchases();
    // If no credit purchases, try getRemaining as fallback
    const allPayments = payments.length > 0 ? payments : creditPurchasesStorage.getRemaining();
    setRemainingPayments(allPayments);
    console.log('Loaded credit purchases:', allPayments); // Debug log
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const purchaseData = {
        distributor_name: formData.distributorName,
        contact_number: formData.contactNumber,
        payment_date: formData.paymentDate,
        amount: parseFloat(formData.amount || 0),
        payment_type: formData.paymentType,
        notes: formData.notes,
        remaining_amount: formData.paymentType === 'Credit' ? parseFloat(formData.amount || 0) : 0,
      };

      const created = creditPurchasesStorage.create(purchaseData);
      console.log('Created purchase:', created); // Debug log
      setSuccess('Credit Purchase recorded successfully!');
      
      // Reset form
      setFormData({
        distributorName: '',
        contactNumber: '',
        paymentDate: new Date().toISOString().split('T')[0],
        amount: '0.00',
        paymentType: 'Cash',
        notes: '',
      });
      
      // Reload after a small delay to ensure data is saved
      setTimeout(() => {
        loadRemainingPayments();
      }, 100);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to record purchase. Please try again.');
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this payment record?')) {
      try {
        creditPurchasesStorage.delete(id);
        setSuccess('Payment record deleted successfully!');
        loadRemainingPayments();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('Failed to delete record. Please try again.');
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const totalPurchase = parseFloat(formData.amount || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return (
    <div className="credit-purchases">
      <div className="credit-purchases-header">
        <h1 className="page-title">Credit Purchases</h1>
      </div>

      <div className="credit-purchases-content">
        {error && (
          <div style={{ 
            color: '#ef4444', 
            padding: '10px', 
            marginBottom: '15px', 
            backgroundColor: '#fee2e2', 
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ 
            color: '#10b981', 
            padding: '10px', 
            marginBottom: '15px', 
            backgroundColor: '#d1fae5', 
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            {success}
          </div>
        )}

        <div className="credit-purchases-card">
          <h2 className="card-title">Record New Purchase/Expense</h2>
          <form onSubmit={handleSubmit} className="credit-purchases-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="distributorName">Distributor Name</label>
                <input
                  type="text"
                  id="distributorName"
                  name="distributorName"
                  value={formData.distributorName}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter distributor name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contactNumber">Contact Number</label>
                <input
                  type="text"
                  id="contactNumber"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter contact number"
                />
              </div>
              <div className="form-group">
                <label htmlFor="paymentDate">Payment Date</label>
                <input
                  type="date"
                  id="paymentDate"
                  name="paymentDate"
                  value={formData.paymentDate}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="amount">Amount</label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label htmlFor="paymentType">Payment Type</label>
                <select
                  id="paymentType"
                  name="paymentType"
                  value={formData.paymentType}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option>Cash</option>
                  <option>Credit</option>
                  <option>UPI</option>
                  <option>Bank Transfer</option>
                </select>
              </div>
            </div>

            <div className="form-row full-width">
              <div className="form-group">
                <label htmlFor="notes">Optional Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="form-input"
                  rows="3"
                  placeholder="Enter any relevant notes..."
                />
              </div>
            </div>

            <div className="total-purchase">
              <span className="total-label">Total Purchase:</span>
              <span className="total-amount">₹ {totalPurchase}</span>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                Save Record
              </button>
            </div>
          </form>
        </div>

        <div className="remaining-payments-card">
          <h2 className="card-title">Credit Purchases Records</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Distributor Name</th>
                <th>Contact</th>
                <th>Amount (₹)</th>
                <th>Remaining (₹)</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {remainingPayments.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                    No credit purchases found. Create a purchase with Payment Type = "Credit" to see it here.
                  </td>
                </tr>
              ) : (
                remainingPayments.map((payment) => (
                  <tr key={payment.id}>
                    <td>{payment.payment_date || (payment.created_at ? new Date(payment.created_at).toISOString().split('T')[0] : '')}</td>
                    <td>{payment.distributor_name || payment.distributorName || 'N/A'}</td>
                    <td>{payment.contact_number || payment.contactNumber || 'N/A'}</td>
                    <td className="amount-cell">{formatCurrency(payment.amount || 0)}</td>
                    <td className="amount-cell">{formatCurrency(payment.remaining_amount || payment.remainingAmount || 0)}</td>
                    <td>{payment.notes || ''}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="action-btn delete-btn" 
                          title="Delete"
                          onClick={() => handleDelete(payment.id)}
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CreditPurchases;

